import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { ideaImagePath } from "@/lib/tilbudsarbeidsrom/idea-board";

const BUCKET = "offer-media";
const SIGNED_URL_TTL_SECONDS = 60 * 60;

/**
 * Signerte adresser for bildene på Idé-tavlen, i ett kall. Bildene ligger i
 * den private bøtta offer-media (VIB-2980), aldri i JSON.
 */
export function useOfferIdeaImageUrls(paths: string[]) {
  const key = [...new Set(paths)].sort();
  return useQuery({
    queryKey: ["offer-idea-images", key.join(",")],
    enabled: key.length > 0,
    // Adressene lever en time; hent nye godt før de dør.
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(key, SIGNED_URL_TTL_SECONDS);
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const row of data ?? []) {
        if (row.path && row.signedUrl && !row.error) map[row.path] = row.signedUrl;
      }
      return map;
    },
  });
}

/** Laster opp et bilde til tilbudets mappe i offer-media og returnerer stien. */
export function useUploadOfferIdeaImage(offerId: string) {
  return useMutation({
    mutationFn: async (file: File): Promise<string> => {
      const path = ideaImagePath(offerId, file.type, crypto.randomUUID());
      if (!path) throw new Error("Bare JPG, PNG, WebP og GIF kan lastes opp.");
      const { error } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type });
      if (error) throw error;
      return path;
    },
    onError: (err) => toast.error(`Kunne ikke laste opp bildet: ${err instanceof Error ? err.message : "ukjent feil"}`),
  });
}
